import React, { useState } from 'react';
import { formatCurrency } from '../utils/format';

export interface EscrowManagerProps {
  totalEscrowed: string;
  totalTasks: number;
  platformFeeBps: number;
  className?: string;
  onCreateEscrow?: (agentId: number, amountUsdc: number, deadlineDays: number, taskDescription: string) => void;
  onDispute?: (escrowId: number) => void;
  onReclaim?: (escrowId: number) => void;
}

/**
 * Escrow Settlement Manager Panel
 * Create escrows, dispute, and reclaim after deadline.
 */
export function EscrowManager({
  totalEscrowed,
  totalTasks,
  platformFeeBps,
  className = '',
  onCreateEscrow,
  onDispute,
  onReclaim,
}: EscrowManagerProps) {
  const [agentId, setAgentId] = useState('0');
  const [amount, setAmount] = useState('');
  const [deadlineDays, setDeadlineDays] = useState('7');
  const [taskDesc, setTaskDesc] = useState('');
  const [disputeId, setDisputeId] = useState('');
  const [reclaimId, setReclaimId] = useState('');

  const feePercent = (platformFeeBps / 100).toFixed(1);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onCreateEscrow || !amount || !taskDesc) return;
    onCreateEscrow(Number(agentId), Number(amount), Number(deadlineDays), taskDesc);
    setAmount('');
    setTaskDesc('');
  };

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Escrow Settlement</h2>
        <span className="text-xs text-gray-500">{feePercent}% platform fee</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <div className="text-sm text-gray-400 mb-1">Total Escrowed</div>
          <div className="text-2xl font-bold text-white">
            {formatCurrency(Number(totalEscrowed))}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-400 mb-1">Total Tasks</div>
          <div className="text-2xl font-bold text-white">{totalTasks}</div>
        </div>
      </div>

      {/* Create Escrow Form */}
      {onCreateEscrow && (
        <form onSubmit={handleCreate} className="mb-6 p-4 bg-gray-800/50 rounded-lg space-y-3">
          <div className="text-sm text-gray-400 font-medium mb-1">Create Escrow</div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">Agent ID</label>
              <input
                type="number"
                min="0"
                value={agentId}
                onChange={e => setAgentId(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Amount (USDC)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="100"
                className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Deadline (days)</label>
              <input
                type="number"
                min="1"
                max="90"
                value={deadlineDays}
                onChange={e => setDeadlineDays(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500">Task Description</label>
            <input
              type="text"
              value={taskDesc}
              onChange={e => setTaskDesc(e.target.value)}
              placeholder="HVAC maintenance — 123 Main St"
              className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          <button
            type="submit"
            disabled={!amount || !taskDesc}
            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded transition-colors text-sm"
          >
            Create Escrow
          </button>
        </form>
      )}

      {/* Dispute / Reclaim Actions */}
      <div className="grid grid-cols-2 gap-4">
        {onDispute && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <div className="text-xs text-gray-500 mb-2">Dispute Escrow</div>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={disputeId}
                onChange={e => setDisputeId(e.target.value)}
                placeholder="ID"
                className="flex-1 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-red-500"
              />
              <button
                onClick={() => { if (disputeId) { onDispute(Number(disputeId)); setDisputeId(''); } }}
                disabled={!disputeId}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs rounded transition-colors"
              >
                Dispute
              </button>
            </div>
          </div>
        )}

        {onReclaim && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <div className="text-xs text-gray-500 mb-2">Reclaim Escrow</div>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={reclaimId}
                onChange={e => setReclaimId(e.target.value)}
                placeholder="ID"
                className="flex-1 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
              />
              <button
                onClick={() => { if (reclaimId) { onReclaim(Number(reclaimId)); setReclaimId(''); } }}
                disabled={!reclaimId}
                className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs rounded transition-colors"
              >
                Reclaim
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
